import { useRef } from 'react';
import TabOptionWithSelect from 'src/components/primitives/Tabs/TabOptionWithSelect';
import TabIndicator from 'src/components/primitives/Tabs/TabIndicator';
import { TabsProps } from 'src/components/primitives/Tabs/types';
import Tag from 'src/components/primitives/Tag/Tag';
import { cm } from 'src/utils/classNames';
import { Z_INDEX } from 'src/constants/zIndex';
import { typedMemo } from 'src/utils/react';
import useTabOptionsForScreenSize from 'src/components/primitives/Tabs/hooks/useTabOptionsForScreenSize';

const TAB_STYLE = {
	zIndex: Z_INDEX.tab,
};

function Tabs<T extends string, S extends string>({
	size = 'md',
	variant = 'primary',
	indicatorTrack,
	activeTab,
	onTabChange,
	options,
	className,
	dataTestId,
	tabTestIdPrefix,
	indicatorClassName,
}: TabsProps<T, S>) {
	const tabOptionsForScreenSize = useTabOptionsForScreenSize({
		options,
		onTabChange,
		activeTab,
	});

	// `tabListRef` and `activeIndex` are used by the animated indicator to derive
	// its styles (e.g. position, width, etc.).
	const tabListRef = useRef<HTMLDivElement>(null);
	const activeIndex = tabOptionsForScreenSize.findIndex(
		(option) => option.value === activeTab
	);

	const variantClassName = {
		primary: 'gap-x-md',
		secondary: 'gap-x-sm',
	}[variant];

	const sizeClassName = {
		sm: 'typo-label-xs py-xs px-md',
		md: 'typo-label-sm py-sm px-md',
		lg: 'typo-label-md py-md px-lg',
	}[size];

	return (
		<div
			className={cm(
				'relative isolate flex items-center',
				variantClassName,
				className
			)}
			ref={tabListRef}
			data-testid={dataTestId}
		>
			{tabOptionsForScreenSize.map(
				({
					value,
					label,
					tag,
					selectOptions,
					selectedValue,
					onSelectedValueChange,
					className: optionClassName,
					disabled,
				}) => {
					const buttonClassName = cm(
						'flex items-center gap-x-xs transition-colors',
						'w-full justify-center border-transparent whitespace-nowrap',
						sizeClassName,
						!disabled
							? 'text-text-secondary hover:text-purple-link data-[active=true]:text-text-default'
							: 'text-text-disabled',
						optionClassName
					);

					if (selectOptions) {
						return (
							<div
								// `key` includes `activeTab` so the inner Select resets when the
								// tab changes and doesn't retain a stale selected value.
								key={`slider-${activeTab}`}
								data-active={value === activeTab}
								// `py-0`: the inner Select.Trigger sets its own height; an outer
								// vertical padding would misalign the animated indicator.
								className={cm(buttonClassName, 'py-0')}
								style={TAB_STYLE}
								data-testid={
									tabTestIdPrefix ? `${tabTestIdPrefix}-${value}` : undefined
								}
							>
								<TabOptionWithSelect
									selectOptions={selectOptions}
									selectedValue={selectedValue}
									onSelectedValueChange={onSelectedValueChange}
									label={label}
									disabled={disabled}
									testIdPrefix={
										tabTestIdPrefix ? `${tabTestIdPrefix}-select` : undefined
									}
								/>
							</div>
						);
					}

					return (
						<button
							key={value}
							className={buttonClassName}
							data-active={value === activeTab}
							onClick={() => onTabChange(value)}
							disabled={disabled}
							style={TAB_STYLE}
							data-testid={
								tabTestIdPrefix ? `${tabTestIdPrefix}-${value}` : undefined
							}
						>
							{label}
							{tag && <Tag {...tag} />}
						</button>
					);
				}
			)}
			<TabIndicator
				activeIndex={activeIndex}
				variant={variant}
				size={size}
				ref={tabListRef}
				indicatorTrack={!!indicatorTrack}
				indicatorClassName={indicatorClassName}
			/>
		</div>
	);
}

export default typedMemo(Tabs);
